import { maybe, rd, type Maybe } from '@passionware/monads';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AppScopeContext,
  useAppScopeFromContext
} from 'v5/core/app-scope/_private/AppScopeContext';
import {
  isInitializedAppScope,
  type InternalAppScope
} from 'v5/core/app-scope/_private/internalAppScope';
import { appScopeFromPathname } from 'v5/core/app-scope/appScopeFromPathname';
import { appScopeUtils, type AppScope } from 'v5/core/app-scope/appScope.types';
import { EnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { useResolvedAppScope as useResolveSlugAppScope } from 'v5/services/internal/AppScopeService/_private/useResolvedAppScope';
import { AppScopeService } from './AppScopeService';

function maybeInitializedSlugScope(
  internalScope: InternalAppScope | null
): Maybe<AppScope> {
  if (!internalScope || !isInitializedAppScope(internalScope)) {
    return maybe.ofAbsent();
  }
  return maybe.of(internalScope);
}

export function createAppScopeService(config: {
  enterpriseService: EnterpriseService;
  useAuthOrganizationId: () => string | undefined;
}): AppScopeService {
  const { enterpriseService, useAuthOrganizationId } = config;

  return {
    useResolvedAppScope() {
      const slugScope = useAppScopeFromContext();
      return useResolveSlugAppScope(maybe.of(slugScope), enterpriseService);
    },
    useSlugAppScope() {
      return useAppScopeFromContext();
    },
    useOrganizationId() {
      return useAppScopeFromContext().organizationId;
    },
    useUrlAppScope() {
      const { pathname } = useLocation();
      const organizationId = useAuthOrganizationId();
      if (!organizationId) {
        return maybe.ofAbsent();
      }
      return maybe.of(appScopeFromPathname(pathname, organizationId));
    },
    useResolvedScopeIdentity() {
      const slugScope = maybeInitializedSlugScope(useContext(AppScopeContext));
      return rd.mapOrElse(
        useResolveSlugAppScope(slugScope, enterpriseService),
        appScopeUtils.identityFromResolved,
        maybe.ofAbsent()
      );
    },
    useMaybeOrganizationId() {
      const slugScope = maybeInitializedSlugScope(useContext(AppScopeContext));
      return maybe.map(slugScope, scope => scope.organizationId);
    },
    useOrganizationScopeIdentity() {
      return maybe.map(
        maybeInitializedSlugScope(useContext(AppScopeContext)),
        scope =>
          appScopeUtils.identityFromEntityWorkspace(scope.organizationId, null)
      );
    }
  };
}
